import React, { useEffect, useState } from "react";
import {
    Plus, ShieldCheck, Edit3, Trash2, Upload, HeartHandshake, X as XIcon,
    HeartCrack, Repeat, UserCheck, ShieldOff, Circle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, PrimaryButton, GhostButton } from "../../components/shell";

const STATUS_META = {
    active:      { label: "Active",      tone: "active",     Icon: UserCheck },
    injured:     { label: "Injured",     tone: "unpaid",     Icon: HeartCrack },
    on_loan:     { label: "On loan",     tone: "pending",    Icon: Repeat },
    trial:       { label: "On trial",    tone: "neutral",    Icon: Circle },
    suspended:   { label: "Suspended",   tone: "cancelled",  Icon: ShieldOff },
    unavailable: { label: "Unavailable", tone: "paused",     Icon: Circle },
};

const POSITION_LABEL = { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", FWD: "Forward" };
const POSITION_ORDER = ["GK", "DEF", "MID", "FWD"];

export default function PlayersTab({ tenant, queryString, sponsors = [] }) {
    const [players, setPlayers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null); // player being edited or "new"
    const apiBase = process.env.REACT_APP_BACKEND_URL;

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/players${queryString || ""}`);
            setPlayers(data.data || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, [tenant?.id, queryString]);

    const onSave = async (draft, id) => {
        try {
            if (id) {
                await api.patch(`/players/${id}${queryString || ""}`, draft);
            } else {
                await api.post(`/players${queryString || ""}`, draft);
            }
            setEditing(null);
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Remove this player from the roster? They will disappear from the fan app too.")) return;
        try {
            await api.delete(`/players/${id}${queryString || ""}`);
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const onReconcile = async () => {
        if (!window.confirm("Clean up the fan app roster? This removes any players from the fan app that are no longer in Floodlight. Safe to run — nothing here will be touched.")) return;
        try {
            const { data } = await api.post(`/players/reconcile${queryString || ""}`);
            if (data.orphans_found === 0) {
                alert("Already in sync — nothing to remove.");
            } else {
                alert(`Removed ${data.removed.length} stale player${data.removed.length === 1 ? "" : "s"} from the fan app.`);
            }
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const grouped = POSITION_ORDER.map((pos) => ({
        pos,
        label: POSITION_LABEL[pos],
        players: players.filter((p) => p.position === pos),
    })).filter((g) => g.players.length > 0);

    const firebaseConfigured = !!tenant?.firebase_club_slug;

    return (
        <div className="space-y-6" data-testid="players-tab">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)] mb-1">Squad</div>
                    <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Players</h1>
                    <p className="text-sm text-zinc-400 mt-1">
                        Manage the current squad — statuses and player sponsors flow straight to your fan app in real time.
                    </p>
                </div>
                <PrimaryButton tenantTheme onClick={() => setEditing("new")} data-testid="players-add-btn">
                    <Plus size={14} strokeWidth={2.4} /> Add player
                </PrimaryButton>
            </div>

            {firebaseConfigured && (
                <div className="flex justify-end">
                    <button
                        onClick={onReconcile}
                        className="text-[11px] uppercase tracking-ultra-wide text-zinc-400 hover:text-white border hairline px-3 py-1.5 inline-flex items-center gap-1.5"
                        data-testid="players-reconcile-btn"
                        title="Purge fan-app player docs that are no longer in Floodlight"
                    >
                        <Repeat size={11} /> Reconcile fan app roster
                    </button>
                </div>
            )}

            {!firebaseConfigured && (
                <div className="border hairline border-amber-500/30 text-amber-200 p-4 text-xs">
                    <strong>Firebase not configured yet.</strong> Set <code className="font-mono text-amber-300">firebase_club_slug</code> in Settings so the fan app can read this roster from Firestore. Players will still save in Floodlight — they just won&apos;t sync to the app until that&apos;s set.
                </div>
            )}

            {err && <div className="border border-rose-500/40 text-rose-300 p-4 text-sm">{err}</div>}

            {busy && players.length === 0 && <div className="text-sm text-zinc-500">Loading roster…</div>}

            {!busy && players.length === 0 && (
                <div className="border hairline p-10 text-center">
                    <ShieldCheck size={28} className="mx-auto mb-3 text-zinc-500" />
                    <div className="font-display font-bold text-lg">No players yet</div>
                    <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">Add your first player — name, shirt number, position and a photo. They&apos;ll appear here in position groups and land in the fan app immediately.</p>
                </div>
            )}

            {grouped.map((g) => (
                <section key={g.pos} className="space-y-3" data-testid={`players-group-${g.pos}`}>
                    <div className="flex items-baseline justify-between">
                        <h2 className="font-display font-bold text-lg tracking-tight text-white">
                            {g.label}s <span className="text-zinc-500 font-normal text-xs ml-2">{g.players.length}</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {g.players.map((p) => (
                            <PlayerCard key={p.id} player={p} apiBase={apiBase} onEdit={() => setEditing(p)} onDelete={() => onDelete(p.id)} />
                        ))}
                    </div>
                </section>
            ))}

            {editing && (
                <PlayerEditModal
                    initial={editing === "new" ? null : editing}
                    sponsors={sponsors}
                    tenant={tenant}
                    queryString={queryString}
                    onClose={() => setEditing(null)}
                    onSave={onSave}
                    onPhotoUploaded={load}
                />
            )}
        </div>
    );
}


const PlayerCard = ({ player, apiBase, onEdit, onDelete }) => {
    const meta = STATUS_META[player.status] || STATUS_META.active;
    return (
        <div className="border hairline bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.035)] transition-colors flex flex-col" data-testid={`player-card-${player.id}`}>
            <div className="relative aspect-square bg-[#050505] overflow-hidden">
                {player.photo_url ? (
                    <img src={player.photo_url} alt={player.name} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-700">
                        <ShieldCheck size={48} strokeWidth={1.2} />
                    </div>
                )}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {typeof player.shirt_number === "number" && (
                        <span className="font-display font-extrabold text-2xl bg-black/70 backdrop-blur-sm text-white w-9 h-9 flex items-center justify-center tracking-tightest">
                            {player.shirt_number}
                        </span>
                    )}
                </div>
                <div className="absolute top-2 right-2">
                    <StatusIconPill meta={meta} />
                </div>
                {player.sponsor_name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-3 py-2">
                        <div className="text-[9px] uppercase tracking-ultra-wide text-[var(--sb-amber)]">Sponsored by</div>
                        <div className="text-xs text-white truncate">{player.sponsor_name}</div>
                    </div>
                )}
            </div>
            <div className="p-3 flex-1 flex flex-col">
                <div className="font-display font-bold tracking-tight text-white truncate">{player.name}</div>
                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mt-0.5">{POSITION_LABEL[player.position] || player.position}</div>
                {player.description && <div className="text-[11px] text-zinc-400 mt-2 line-clamp-2">{player.description}</div>}
                <div className="mt-3 flex items-center justify-end gap-1">
                    <button onClick={onEdit} className="text-zinc-400 hover:text-white p-1.5" aria-label="Edit"><Edit3 size={13} /></button>
                    <button onClick={onDelete} className="text-rose-400 hover:text-rose-300 p-1.5" aria-label="Delete"><Trash2 size={13} /></button>
                </div>
            </div>
        </div>
    );
};


const StatusIconPill = ({ meta }) => {
    const { Icon, label, tone } = meta;
    return (
        <span className="inline-flex items-center gap-1 bg-black/70 backdrop-blur-sm px-2 py-1" data-testid={`player-status-${label.toLowerCase()}`}>
            <Icon size={11} className={
                tone === "active" ? "text-emerald-300"
                : tone === "unpaid" ? "text-rose-300"
                : tone === "pending" ? "text-amber-300"
                : "text-zinc-400"
            } />
            <span className="text-[10px] uppercase tracking-ultra-wide text-white">{label}</span>
        </span>
    );
};


const PlayerEditModal = ({ initial, sponsors, tenant, queryString, onClose, onSave, onPhotoUploaded }) => {
    const [draft, setDraft] = useState({
        name: initial?.name || "",
        shirt_number: initial?.shirt_number ?? "",
        position: initial?.position || "MID",
        description: initial?.description || "",
        sponsor_id: initial?.sponsor_id || "",
        status: initial?.status || "active",
        joined_date: initial?.joined_date || "",
        dob: initial?.dob || "",
    });
    const [uploading, setUploading] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (!draft.name.trim()) return alert("Player name is required");
        const payload = {
            ...draft,
            shirt_number: draft.shirt_number === "" ? null : Number(draft.shirt_number),
            sponsor_id: draft.sponsor_id || null,
            joined_date: draft.joined_date || null,
            dob: draft.dob || null,
        };
        onSave(payload, initial?.id);
    };

    const uploadPhoto = async (file) => {
        if (!file || !initial?.id) return;
        if (!tenant?.firebase_club_slug) {
            alert("Set your Firebase club slug in Settings first — photos upload to Firebase Storage.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/players/${initial.id}/photo${queryString || ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            onPhotoUploaded && onPhotoUploaded();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Modal open onClose={onClose} title={initial ? `Edit ${initial.name}` : "Add player"} testId="player-edit-modal">
            <form onSubmit={submit} className="space-y-4">
                <Row>
                    <Field label="Name *" span={8}>
                        <Input value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Full name" testId="player-name" />
                    </Field>
                    <Field label="Shirt #" span={4}>
                        <Input type="number" value={draft.shirt_number} onChange={(v) => setDraft({ ...draft, shirt_number: v })} placeholder="10" testId="player-shirt" />
                    </Field>
                </Row>
                <Row>
                    <Field label="Position *" span={6}>
                        <Select value={draft.position} onChange={(v) => setDraft({ ...draft, position: v })} testId="player-position"
                            options={POSITION_ORDER.map((p) => ({ value: p, label: `${p} — ${POSITION_LABEL[p]}` }))} />
                    </Field>
                    <Field label="Status" span={6}>
                        <Select value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} testId="player-status"
                            options={Object.entries(STATUS_META).map(([k, m]) => ({ value: k, label: m.label }))} />
                    </Field>
                </Row>
                <Field label="Player sponsor" hint="Link to an existing sponsor (they'll be credited on the player card in the fan app).">
                    <Select value={draft.sponsor_id} onChange={(v) => setDraft({ ...draft, sponsor_id: v })} testId="player-sponsor"
                        options={[{ value: "", label: "— no sponsor —" }, ...sponsors.map((s) => ({ value: s.id, label: s.name }))]} />
                </Field>
                <Field label="Description" hint="Short bio — appears on the player detail screen in the app.">
                    <Textarea value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} rows={3} placeholder="e.g. Academy graduate, 4th season with the first team, top scorer in 2024/25." testId="player-desc" />
                </Field>
                <Row>
                    <Field label="Joined" span={6}>
                        <Input type="date" value={draft.joined_date} onChange={(v) => setDraft({ ...draft, joined_date: v })} testId="player-joined" />
                    </Field>
                    <Field label="DOB" span={6}>
                        <Input type="date" value={draft.dob} onChange={(v) => setDraft({ ...draft, dob: v })} testId="player-dob" />
                    </Field>
                </Row>
                {initial && (
                    <div className="border hairline p-3 flex items-center gap-3" data-testid="player-photo-uploader">
                        <div className="w-16 h-16 border hairline bg-[#050505] overflow-hidden flex items-center justify-center">
                            {initial.photo_url ? <img src={initial.photo_url} alt="" className="w-full h-full object-cover" /> : <ShieldCheck size={22} className="text-zinc-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Photo</div>
                            <div className="text-xs text-zinc-400">Uploads to Firebase Storage at <span className="font-mono text-zinc-300">clubs/{tenant?.firebase_club_slug || "…"}/players/{initial.team_slug}/{initial.id}</span></div>
                        </div>
                        <label className="inline-flex items-center gap-2 border hairline px-3 py-2 cursor-pointer hover:border-white/30 text-xs uppercase tracking-ultra-wide">
                            <Upload size={12} /> {uploading ? "…" : "Upload"}
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={uploading} />
                        </label>
                    </div>
                )}
                {!initial && (
                    <div className="text-[11px] italic text-zinc-500">You can upload a photo after saving the player.</div>
                )}
                <div className="flex items-center justify-end gap-2 border-t hairline pt-4">
                    <GhostButton type="button" onClick={onClose}>
                        <XIcon size={14} /> Cancel
                    </GhostButton>
                    <PrimaryButton tenantTheme type="submit" data-testid="player-save-btn">
                        <HeartHandshake size={14} strokeWidth={2.4} /> {initial ? "Save changes" : "Add to squad"}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

const Row = ({ children }) => <div className="grid grid-cols-12 gap-3">{children}</div>;
const Field = ({ label, span = 12, hint, children }) => (
    <div className={`col-span-12 md:col-span-${span}`}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">{label}</div>
        {children}
        {hint && <div className="text-[10px] text-zinc-500 italic mt-1 leading-relaxed">{hint}</div>}
    </div>
);
const Input = ({ value, onChange, testId, ...rest }) => (
    <input {...rest} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30" />
);
const Textarea = ({ value, onChange, testId, ...rest }) => (
    <textarea {...rest} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30 resize-none" />
);
const Select = ({ value, onChange, options, testId }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);
